import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Send, Copy as CopyIcon, CheckCircle, BellRing } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ManageContent = () => {
  const [activeTab, setActiveTab] = useState('testimonials');
  const [testimonials, setTestimonials] = useState([]);
  const [draftTestimonials, setDraftTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      const authHeaders = { 'Authorization': `Basic ${btoa(':' + password)}` };
      const [testimonialsRes, draftsRes, partnersRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/testimonials`),
        fetch(`${API_URL}/api/admin/testimonials/drafts`, { headers: authHeaders }),
        fetch(`${API_URL}/api/partners`),
        fetch(`${API_URL}/api/services`)
      ]);

      setTestimonials(await testimonialsRes.json());
      if (draftsRes.ok) setDraftTestimonials(await draftsRes.json());
      setPartners(await partnersRes.json());
      setServices(await servicesRes.json());
    } catch (error) {
      logger.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/${type}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(':Pawsome2026!')
        }
      });

      if (response.ok) {
        fetchAllContent();
        alert('Item deleted successfully!');
      }
    } catch (error) {
      logger.error('Error deleting:', error);
      alert('Failed to delete item');
    }
  };

  const handleSave = async (type, data) => {
    try {
      const isEdit = Boolean(data.id);
      const url = isEdit 
        ? `${API_URL}/api/admin/${type}/${data.id}`
        : `${API_URL}/api/admin/${type}`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(':Pawsome2026!')
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        fetchAllContent();
        setEditingItem(null);
        setShowAddForm(false);
        alert(`Item ${isEdit ? 'updated' : 'created'} successfully!`);
      }
    } catch (error) {
      logger.error('Error saving:', error);
      alert('Failed to save item');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading content...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600">Manage testimonials, partners, and services</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`pb-3 px-1 border-b-2 font-medium ${
              activeTab === 'testimonials'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Testimonials ({testimonials.length})
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`pb-3 px-1 border-b-2 font-medium ${
              activeTab === 'partners'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Partners ({partners.length})
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`pb-3 px-1 border-b-2 font-medium ${
              activeTab === 'services'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Services ({services.length})
          </button>
        </nav>
      </div>

      {/* Testimonials Tab */}
      {activeTab === 'testimonials' && (
        <TestimonialsManager
          testimonials={testimonials}
          drafts={draftTestimonials}
          password={password}
          onRefresh={fetchAllContent}
          onDelete={(id) => handleDelete('testimonials', id)}
          onSave={(data) => handleSave('testimonials', data)}
        />
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <PartnersManager
          partners={partners}
          onDelete={(id) => handleDelete('partners', id)}
          onSave={(data) => handleSave('partners', data)}
        />
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <ServicesManager
          services={services}
          onDelete={(id) => handleDelete('services', id)}
          onSave={(data) => handleSave('services', data)}
        />
      )}
    </div>
  );
};

// Testimonials Manager Component
const TestimonialsManager = ({ testimonials, drafts = [], password, onRefresh, onDelete, onSave }) => {
  const [showForm, setShowForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({ name: '', email: '', kind: 'collaborator', location: '', role: '', custom_message: '' });
  const [requesting, setRequesting] = useState(false);
  const [formData, setFormData] = useState({});

  const handleAdd = () => {
    setFormData({
      name: '',
      location: '',
      kind: 'client',
      role: '',
      pet: '',
      petType: 'dog',
      rating: 5,
      text: '',
      date: '',
      order: 0
    });
    setShowForm(true);
  };

  const handleEdit = (testimonial) => {
    setFormData({ kind: 'client', ...testimonial });
    setShowForm(true);
  };

  const isClient = (formData.kind || 'client') === 'client';

  const handleRequestTestimonial = async () => {
    if (!requestData.name.trim() || !requestData.email.trim()) {
      alert('Please enter a name and email.');
      return;
    }
    setRequesting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify(requestData),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      alert(`✓ ${body.message}\n\nDirect link (for your records):\n${body.link}`);
      setShowRequestForm(false);
      setRequestData({ name: '', email: '', kind: 'collaborator', location: '', role: '', custom_message: '' });
      onRefresh?.();
    } catch (e) {
      alert(`Failed to send request: ${e.message}`);
    } finally {
      setRequesting(false);
    }
  };

  const handleApproveDraft = async (id) => {
    if (!window.confirm('Publish this testimonial to the public site?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials/${id}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onRefresh?.();
    } catch (e) {
      alert(`Failed to publish: ${e.message}`);
    }
  };

  const handleRemindDraft = async (draft) => {
    let email = (draft.email || '').trim();
    if (!email) {
      email = (window.prompt(
        `No email is on record for ${draft.name || 'this contributor'}. Enter their email to send the reminder:`
      ) || '').trim();
      if (!email) return;
    } else if (!window.confirm(`Send a reminder email to ${email}?`)) {
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials/${draft.id}/remind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify({ email }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      alert(data.message || `Reminder sent to ${email}.`);
      onRefresh?.();
    } catch (e) {
      alert(`Failed to send reminder: ${e.message}`);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Testimonials</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowRequestForm(true)} variant="outline" className="flex items-center gap-2" data-testid="request-testimonial-btn">
            <Send className="w-4 h-4" />
            Request Testimonial
          </Button>
          <Button onClick={handleAdd} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Testimonial
          </Button>
        </div>
      </div>

      {/* Request Testimonial Modal */}
      {showRequestForm && (
        <Card className="mb-6 border-primary/40" data-testid="request-testimonial-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Send className="w-4 h-4" /> Email a testimonial request</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground/70 mb-3">
              Sends a one-off email to the partner with a tokenised link to a public form. Their submission lands here as a Draft for your approval before it goes live.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground/70 mb-1">Type</label>
                <div className="flex gap-2 flex-wrap">
                  {['client', 'broker', 'collaborator'].map(k => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setRequestData({ ...requestData, kind: k })}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        requestData.kind === k
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-foreground/70 border-border hover:bg-gray-50'
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              <input placeholder="Full Name" value={requestData.name} onChange={e => setRequestData({...requestData, name: e.target.value})} className="border rounded px-3 py-2 text-sm" data-testid="request-name" />
              <input type="email" placeholder="Email address" value={requestData.email} onChange={e => setRequestData({...requestData, email: e.target.value})} className="border rounded px-3 py-2 text-sm" data-testid="request-email" />
              <input placeholder="Location (optional)" value={requestData.location} onChange={e => setRequestData({...requestData, location: e.target.value})} className="border rounded px-3 py-2 text-sm" />
              {requestData.kind !== 'client' && (
                <input placeholder="Role (optional, e.g. Broker at ABC Realty)" value={requestData.role} onChange={e => setRequestData({...requestData, role: e.target.value})} className="border rounded px-3 py-2 text-sm" />
              )}
              <textarea placeholder="Optional custom note in the email" value={requestData.custom_message} onChange={e => setRequestData({...requestData, custom_message: e.target.value})} className="border rounded px-3 py-2 text-sm col-span-2" rows="2" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleRequestTestimonial} disabled={requesting} data-testid="request-submit">
                <Send className="w-4 h-4 mr-2" />
                {requesting ? 'Sending…' : 'Send request'}
              </Button>
              <Button variant="outline" onClick={() => setShowRequestForm(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drafts panel */}
      {drafts.length > 0 && (
        <div className="mb-6 p-4 rounded-xl border-2 border-amber-200 bg-amber-50" data-testid="testimonial-drafts-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-amber-900">Pending approval ({drafts.length})</h3>
          </div>
          <div className="space-y-2">
            {drafts.map(d => (
              <div key={d.id} className="p-3 bg-white rounded-lg border border-amber-200 flex items-start justify-between gap-3" data-testid={`draft-testimonial-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{d.name || <em className="text-foreground/60">(unnamed)</em>}</span>
                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border bg-white text-foreground/60 border-border">{d.kind}</span>
                    {d.submitted_at ? (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">submitted</span>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">awaiting reply</span>
                    )}
                    {d.reminder_count > 0 && !d.submitted_at && (
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" title={d.last_reminded_at ? `Last reminded ${new Date(d.last_reminded_at).toLocaleDateString()}` : ''}>
                        reminded × {d.reminder_count}
                      </span>
                    )}
                  </div>
                  {d.text && <p className="text-sm text-foreground/70 italic mt-1">&ldquo;{d.text.substring(0, 220)}{d.text.length > 220 ? '…' : ''}&rdquo;</p>}
                  {!d.text && d.token && (
                    <p className="text-xs text-foreground/50 mt-1">
                      Link: <code className="bg-gray-100 px-1 rounded">/testimonial/{d.token}</code>
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {d.text && (
                    <button onClick={() => handleApproveDraft(d.id)} title="Publish to public site" className="p-2 text-emerald-700 hover:bg-emerald-50 rounded" data-testid={`publish-draft-${d.id}`}>
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  {!d.submitted_at && d.token && (
                    <button onClick={() => handleRemindDraft(d)} title="Send reminder email" className="p-2 text-amber-600 hover:bg-amber-50 rounded" data-testid={`remind-draft-${d.id}`}>
                      <BellRing className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleEdit(d)} title="Edit before publishing" className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-draft-${d.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(d.id).then(() => onRefresh?.())} title="Delete" className="p-2 text-red-600 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{formData.id ? 'Edit' : 'Add'} Testimonial</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground/70 mb-1">Testimonial type</label>
                <div className="flex gap-2 flex-wrap" data-testid="testimonial-kind-tabs">
                  {[
                    { value: 'client', label: 'Client' },
                    { value: 'broker', label: 'Broker' },
                    { value: 'collaborator', label: 'Collaborator' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, kind: opt.value })}
                      data-testid={`testimonial-kind-${opt.value}`}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        (formData.kind || 'client') === opt.value
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-foreground/70 border-border hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                placeholder={isClient ? "Client Name" : "Full Name"}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder={isClient ? "Location (e.g., UK → USA)" : "Location (e.g., Cayman Islands)"}
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="border rounded px-3 py-2"
              />
              {isClient ? (
                <>
                  <input
                    type="text"
                    placeholder="Pet Name"
                    value={formData.pet || ''}
                    onChange={(e) => setFormData({ ...formData, pet: e.target.value })}
                    className="border rounded px-3 py-2"
                    data-testid="testimonial-pet-input"
                  />
                  <select
                    value={formData.petType || 'dog'}
                    onChange={(e) => setFormData({ ...formData, petType: e.target.value })}
                    className="border rounded px-3 py-2"
                    data-testid="testimonial-pet-type"
                  >
                    <option value="dog">Dog</option>
                    <option value="cat">Cat</option>
                    <option value="bird">Bird</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </>
              ) : (
                <input
                  type="text"
                  placeholder={formData.kind === 'broker' ? 'Role (e.g., Broker at ABC Realty)' : 'Role (e.g., Vet at XYZ Clinic)'}
                  value={formData.role || ''}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="border rounded px-3 py-2 col-span-2"
                  data-testid="testimonial-role-input"
                />
              )}
              <input
                type="text"
                placeholder="Date (e.g., March 2025)"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="number"
                placeholder="Rating (1-5)"
                min="1"
                max="5"
                value={formData.rating || 5}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                className="border rounded px-3 py-2"
              />
            </div>
            <textarea
              placeholder="Testimonial Text"
              value={formData.text || ''}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              className="border rounded px-3 py-2 w-full mt-4"
              rows="4"
            />
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button onClick={() => onSave(formData)} data-testid="testimonial-save-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              {(formData.status === 'draft' || formData.status === 'pending') && formData.id && (
                <Button
                  onClick={() => onSave({ ...formData, status: 'published' })}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="testimonial-save-publish-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save &amp; Publish
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{testimonial.name}</h3>
                    {testimonial.kind && testimonial.kind !== 'client' && (
                      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${
                        testimonial.kind === 'broker'
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}>
                        {testimonial.kind}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  {(testimonial.kind || 'client') === 'client' ? (
                    <p className="text-sm text-gray-600">Pet: {testimonial.pet || '—'} ({testimonial.petType || '—'})</p>
                  ) : testimonial.role ? (
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  ) : null}
                  <p className="text-sm text-gray-500 mt-2 italic">&ldquo;{(testimonial.text || '').substring(0, 150)}{(testimonial.text || '').length > 150 ? '…' : ''}&rdquo;</p>
                  <p className="text-xs text-gray-400 mt-2">Rating: {'⭐'.repeat(Math.max(0, Math.min(5, Number(testimonial.rating) || 0)))} | {testimonial.date || '—'}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(testimonial)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    data-testid={`edit-testimonial-${testimonial.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(testimonial.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    data-testid={`delete-testimonial-${testimonial.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Partners Manager Component (similar structure)
const PartnersManager = ({ partners, onDelete, onSave }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});

  const handleAdd = () => {
    setFormData({ name: '', logo: '', website: '', category: '', order: 0 });
    setShowForm(true);
  };

  const handleEdit = (partner) => {
    setFormData(partner);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Partners</h2>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Partner
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{formData.id ? 'Edit' : 'Add'} Partner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Partner Name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Logo URL"
                value={formData.logo || ''}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Website URL"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Category"
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="border rounded px-3 py-2"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => onSave(formData)}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map((partner) => (
          <Card key={partner.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold">{partner.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(partner.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">{partner.category}</p>
              <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">
                {partner.website}
              </a>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Services Manager Component
const ServicesManager = ({ services, onDelete, onSave }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});

  const handleAdd = () => {
    setFormData({ title: '', description: '', icon: '', features: [], price: '', order: 0 });
    setShowForm(true);
  };

  const handleEdit = (service) => {
    setFormData(service);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Services</h2>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Service
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{formData.id ? 'Edit' : 'Add'} Service</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Service Title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Icon (emoji or class)"
                value={formData.icon || ''}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="border rounded px-3 py-2"
              />
            </div>
            <textarea
              placeholder="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="border rounded px-3 py-2 w-full mt-4"
              rows="3"
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={() => onSave(formData)}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => (
          <Card key={service.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{service.icon}</span>
                  <h3 className="font-bold">{service.title}</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(service)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(service.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">{service.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ManageContent;
